import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { getFriendlyErrorMessage } from "@/lib/api-error";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  type PublicProfile,
  type BookingResult,
  type Provider,
  providerName,
  fieldClass,
  panelClass,
} from "@/components/bookings/types";

// Find the first real instant of the requested date in the visitor's timezone.
// Scanning UTC avoids interpreting the date in the browser/server timezone and
// handles midnight transitions where the first local time is later than 00:00.
function startOfVisitorDate(date: string, timezone: string): Date {
  const format = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const anchor = Date.parse(date + "T00:00:00Z");
  for (
    let at = anchor - 18 * 3600000;
    at <= anchor + 18 * 3600000;
    at += 60000
  ) {
    const parts = format.formatToParts(new Date(at));
    const part = (key: string) => parts.find((p) => p.type === key)!.value;
    if (`${part("year")}-${part("month")}-${part("day")}` === date)
      return new Date(at);
  }
  throw new Error("Choose a valid date in your timezone.");
}

export function PublicBookingPage({ manage = false }: { manage?: boolean }) {
  const { slug } = useParams();
  const { user } = useAuth();
  const [profile, setProfile] = useState<PublicProfile>();
  const [typeId, setTypeId] = useState("");
  const [provider, setProvider] = useState<Provider>("google");
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const [date, setDate] = useState(
    new Date(Date.now() + 86400000).toISOString().slice(0, 10),
  );
  const [available, setAvailable] = useState<string[]>([]);
  const [selected, setSelected] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [working, setWorking] = useState(false);
  const [searched, setSearched] = useState(false);
  const [result, setResult] = useState<BookingResult>();
  const [rescheduling, setRescheduling] = useState(false);
  const requestKey = useRef({ fingerprint: "", key: "" });
  const token = useRef(manage ? window.location.hash.slice(1) : "");
  const run = async (action: () => Promise<void>) => {
    setWorking(true);
    setError("");
    try {
      await action();
    } catch (e) {
      setError(getFriendlyErrorMessage(e));
    } finally {
      setWorking(false);
    }
  };
  useEffect(() => {
    if (user) {
      setName(`${user.firstName} ${user.lastName}`);
      setEmail(user.email);
    }
  }, [user]);
  useEffect(() => {
    void run(async () => {
      if (manage) {
        const b = await api.post<BookingResult>("/bookings/manage", {
          token: token.current,
          action: "view",
        });
        setResult(b);
      } else {
        const p = await api.get<PublicProfile>(
          `/bookings/public/${encodeURIComponent(slug || "")}`,
        );
        setProfile(p);
        setTypeId(p.types[0]?.id || "");
        setProvider(p.types[0]?.providers[0] || "google");
      }
    });
  }, [slug, manage]);
  useEffect(() => {
    setAvailable([]);
    setSelected("");
    setSearched(false);
  }, [date, typeId, provider, timezone]);
  const displayTime = (value: string) => {
    try {
      return new Date(value).toLocaleString(undefined, {
        timeZone: timezone,
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
      });
    } catch {
      return value;
    }
  };
  const search = async () => {
    new Intl.DateTimeFormat("en", { timeZone: timezone });
    const start = startOfVisitorDate(date, timezone);
    const query = new URLSearchParams({
      typeId,
      provider,
      start: start.toISOString(),
      end: new Date(start.getTime() + 7 * 86400000).toISOString(),
    });
    const times = rescheduling
      ? await api.post<string[]>("/bookings/manage", {
          token: token.current,
          action: "availability",
          start: query.get("start"),
          end: query.get("end"),
        })
      : await api.get<string[]>(
          `/bookings/public/${encodeURIComponent(profile!.slug)}/availability?${query}`,
        );
    setAvailable(times);
    setSearched(true);
  };
  const submit = async () => {
    if (rescheduling) {
      setResult(
        await api.post<BookingResult>("/bookings/manage", {
          token: token.current,
          action: "reschedule",
          start: selected,
        }),
      );
      setRescheduling(false);
      return;
    }
    const fingerprint = JSON.stringify([
      typeId,
      provider,
      selected,
      name,
      email,
      timezone,
    ]);
    if (requestKey.current.fingerprint !== fingerprint)
      requestKey.current = { fingerprint, key: crypto.randomUUID() };
    const b = await api.post<BookingResult>(
      `/bookings/public/${encodeURIComponent(profile!.slug)}`,
      {
        typeId,
        provider,
        start: selected,
        name,
        email,
        timezone,
        idempotencyKey: requestKey.current.key,
      },
    );
    token.current = b.manageToken;
    setResult(b);
  };
  return (
    <main className="min-h-screen bg-bg-primary text-text-primary px-4 py-10">
      <div className="max-w-3xl mx-auto space-y-6">
        <header>
          <p className="text-emerald-400 font-semibold tracking-widest text-xs">
            OPUS RECRUITER
          </p>
          <h1 className="text-3xl font-semibold mt-3">
            {result && !rescheduling
              ? "Your booking"
              : rescheduling
                ? "Choose a new time"
                : profile
                  ? `Meet with ${profile.name}`
                  : "Book a meeting"}
          </h1>
          <p className="text-text-secondary mt-2">
            Choose a convenient time. Calendar details stay private.
          </p>
        </header>
        {error && (
          <p
            role="alert"
            className="rounded-lg border border-red-400/30 p-4 text-red-300"
          >
            {error}
          </p>
        )}
        {result && !rescheduling ? (
          <section className={panelClass}>
            <h2 className="font-semibold text-xl">
              {result.status === "confirmed"
                ? "Meeting confirmed"
                : result.status === "cancelled"
                  ? "Meeting cancelled"
                  : "Calendar update in progress"}
            </h2>
            <p>
              {result.title ||
                profile?.types.find((t) => t.id === typeId)?.title}
            </p>
            <p>{displayTime(result.start)}</p>
            <p>{providerName(result.provider)}</p>
            {result.meetingUrl && (
              <a
                className="text-emerald-400 underline"
                href={result.meetingUrl}
                target="_blank"
                rel="noreferrer"
              >
                Join meeting
              </a>
            )}
            {!["confirmed", "cancelled"].includes(result.status) && (
              <p>
                Keep this page or save your management link. Your meeting is
                confirmed only when the calendar update finishes.
              </p>
            )}
            <a
              className="block text-emerald-400 underline"
              href={`/book/manage#${result.manageToken}`}
            >
              Save or open your booking management link
            </a>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                disabled={working}
                onClick={() =>
                  void run(async () =>
                    setResult(
                      await api.post<BookingResult>("/bookings/manage", {
                        token: token.current,
                        action: "view",
                      }),
                    ),
                  )
                }
              >
                Refresh status
              </Button>
              {result.status === "confirmed" && (
                <>
                  <Button
                    variant="outline"
                    disabled={working}
                    onClick={() =>
                      void run(async () => {
                        const details = await api.post<BookingResult>(
                          "/bookings/manage",
                          { token: token.current, action: "view" },
                        );
                        const p = await api.get<PublicProfile>(
                          `/bookings/public/${details.slug}`,
                        );
                        setProfile(p);
                        setTypeId(details.typeId!);
                        setProvider(details.provider);
                        setRescheduling(true);
                        setSelected("");
                        setAvailable([]);
                      })
                    }
                  >
                    Reschedule
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={working}
                    onClick={() =>
                      void run(async () =>
                        setResult(
                          await api.post<BookingResult>("/bookings/manage", {
                            token: token.current,
                            action: "cancel",
                          }),
                        ),
                      )
                    }
                  >
                    Cancel meeting
                  </Button>
                </>
              )}
            </div>
          </section>
        ) : profile ? (
          <form
            className={panelClass}
            onSubmit={(e) => {
              e.preventDefault();
              void run(submit);
            }}
          >
            {!profile.types.length ? (
              <p>
                No meeting types are available yet. Please contact the host.
              </p>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <label>
                    Meeting
                    <select
                      disabled={rescheduling}
                      className={fieldClass}
                      value={typeId}
                      onChange={(e) => {
                        setTypeId(e.target.value);
                        setProvider(
                          profile.types.find((t) => t.id === e.target.value)!
                            .providers[0],
                        );
                      }}
                    >
                      {profile.types.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.title} · {t.duration} min
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Meeting platform
                    <select
                      disabled={rescheduling}
                      className={fieldClass}
                      value={provider}
                      onChange={(e) => setProvider(e.target.value as Provider)}
                    >
                      {profile.types
                        .find((t) => t.id === typeId)
                        ?.providers.map((p) => (
                          <option key={p} value={p}>
                            {providerName(p)}
                          </option>
                        ))}
                    </select>
                  </label>
                  <label>
                    Your timezone
                    <input
                      required
                      className={fieldClass}
                      value={timezone}
                      onChange={(e) => setTimezone(e.target.value)}
                      placeholder="Africa/Johannesburg"
                    />
                  </label>
                  <label>
                    Week starting
                    <input
                      required
                      type="date"
                      className={fieldClass}
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                    />
                  </label>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  disabled={working}
                  onClick={() => void run(search)}
                >
                  {working ? "Checking…" : "Find available times"}
                </Button>
                <div
                  role="group"
                  aria-label="Available times"
                  className="grid sm:grid-cols-2 gap-2 max-h-80 overflow-y-auto"
                >
                  {available.map((at) => (
                    <button
                      type="button"
                      key={at}
                      aria-pressed={selected === at}
                      onClick={() => setSelected(at)}
                      className={`rounded-lg border px-3 py-2 text-sm ${selected === at ? "border-emerald-400 bg-emerald-600 text-white" : "border-white/15 hover:border-emerald-400"}`}
                    >
                      {displayTime(at)}
                    </button>
                  ))}
                </div>
                {searched && !available.length && (
                  <p role="status">
                    No available times in this week. Try another week or meeting
                    platform.
                  </p>
                )}
                {selected && (
                  <div className="space-y-4 border-t border-white/10 pt-4">
                    <p className="text-emerald-400">{displayTime(selected)}</p>
                    {!rescheduling && (
                      <div className="grid sm:grid-cols-2 gap-4">
                        <label>
                          Your name
                          <input
                            required
                            maxLength={150}
                            autoComplete="name"
                            className={fieldClass}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                          />
                        </label>
                        <label>
                          Email for your invitation
                          <input
                            required
                            type="email"
                            maxLength={255}
                            autoComplete="email"
                            className={fieldClass}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                          />
                        </label>
                      </div>
                    )}
                    <Button type="submit" disabled={working}>
                      {working
                        ? "Updating calendar…"
                        : rescheduling
                          ? "Confirm new time"
                          : "Confirm booking"}
                    </Button>
                  </div>
                )}
              </>
            )}
          </form>
        ) : (
          !error && <p>Loading…</p>
        )}
        {user && (
          <Link to="/bookings" className="text-emerald-400 underline">
            Back to CRM bookings
          </Link>
        )}
      </div>
    </main>
  );
}
